import React, { useState } from 'react';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { addonService } from '../services/addonService';
import { SubscriptionAddon } from '../types/subscription';
import { ADDON_LIMITS } from '../constants/addonLimits';

interface AddonManagementProps {
  onAddonCancelled?: () => void;
}

const AddonManagement: React.FC<AddonManagementProps> = ({ onAddonCancelled }) => {
  const { subscription, addons, refreshSubscription } = useSubscriptionContext();
  const [cancellingAddon, setCancellingAddon] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!subscription || subscription.status !== 'active') {
    return null;
  }

  const activeAddons = addons.filter(addon => addon.status === 'active');

  const handleCancelAddon = async (addonId: number) => {
    if (!subscription) return;

    if (!window.confirm('Are you sure you want to cancel this addon? It will remain active until the end of your billing period.')) {
      return;
    }

    try {
      setCancellingAddon(addonId);
      setError(null);

      await addonService.cancelAddon(subscription.id, addonId);

      // Refresh subscription to get updated addons
      refreshSubscription();

      if (onAddonCancelled) {
        onAddonCancelled();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel addon';
      setError(errorMessage);
      console.error('Error cancelling addon:', err);
    } finally {
      setCancellingAddon(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (priceCents: number) => {
    return `$${(priceCents / 100).toFixed(2)}`;
  };

  const getAddonDisplayName = (addonType: string) => {
    const names: Record<string, string> = {
      tabletop_creator: 'Tabletop Creator',
      iso27001_readiness: 'ISO 27001 Readiness',
      executive_training: 'Executive Training',
      flash_rtc: 'Flash RTC',
      insurer_reports: 'Insurer Reports',
      resilience_badge: 'Resilience Badge',
    };
    return names[addonType] || addonType;
  };

  if (activeAddons.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Active Add-Ons</h3>
        <p className="text-gray-600">You don't have any active add-ons. Purchase add-ons to enhance your subscription.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Active Add-Ons</h3>
        <span className="text-sm text-gray-600">{activeAddons.length} active</span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 rounded-md border border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {activeAddons.map((addon) => (
          <div
            key={addon.id}
            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <h4 className="text-md font-semibold text-gray-900">
                    {getAddonDisplayName(addon.addon_type)}
                  </h4>
                  <span className="ml-2 inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                    Active
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Price:</span> {formatPrice(addon.price_cents)}/year
                  </div>
                  <div>
                    <span className="font-medium">Renews:</span> {formatDate(addon.current_period_end)}
                  </div>
                  {ADDON_LIMITS[addon.addon_type]?.max_scenarios && (
                    <div className="col-span-2">
                      <span className="font-medium">Scenarios used:</span>{' '}
                      {addon.usage_count} / {ADDON_LIMITS[addon.addon_type].max_scenarios}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleCancelAddon(addon.id)}
                disabled={cancellingAddon === addon.id}
                className="ml-4 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancellingAddon === addon.id ? 'Cancelling...' : 'Cancel'}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t text-sm text-gray-600">
        <p>
          Cancelled add-ons will remain active until the end of your current billing period ({formatDate(subscription.current_period_end)}).
        </p>
      </div>
    </div>
  );
};

export default AddonManagement;

